/**
 * The presentational primitives of a finding's verdict story - the prose sections, the expected/actual claims,
 * the observed-app-issues note, the classification-error block, and the code-evidence list. Rendered through the
 * finding drawer's summary tab, which serves both the app-scoped test-result page and the running-stage quick-look
 * drawer, so the two render the same verdict in one voice instead of two drifting copies. Each surface still owns
 * its own section order and media panel; only the atoms live here.
 */
import { cn } from "@autonoma/blacklight";
import type { InvestigationEvidence } from "@autonoma/types";
import { CaretDownIcon } from "@phosphor-icons/react/CaretDown";
import { CheckCircleIcon } from "@phosphor-icons/react/CheckCircle";
import { FileCodeIcon } from "@phosphor-icons/react/FileCode";
import { GitDiffIcon } from "@phosphor-icons/react/GitDiff";
import { ImageIcon } from "@phosphor-icons/react/Image";
import { InfoIcon } from "@phosphor-icons/react/Info";
import { TerminalWindowIcon } from "@phosphor-icons/react/TerminalWindow";
import { VideoCameraIcon } from "@phosphor-icons/react/VideoCamera";
import { XCircleIcon } from "@phosphor-icons/react/XCircle";
import { CodeBlock, evidencePermalink } from "components/investigation/code-block";
import { ScreenshotLightbox } from "components/screenshot-lightbox";
import { type ComponentType, type ReactNode, useState } from "react";

/** The section-title icon size, so every verdict-story heading marks its section at the same scale. */
const SECTION_ICON_SIZE = 13;

/**
 * Whether the observed behavior confirmed the expectation or contradicted it. A passing verdict's actual behavior
 * MATCHED what was expected, so it must not be painted like a failure; a bug's actual behavior is a `divergence`.
 * Drives the "Actual" claim's tone and icon so the pair never reads as an error when the run actually passed.
 */
export type BehaviorOutcome = "match" | "divergence";

/**
 * The app-behavior claims, tinted to their meaning so the expected/actual contrast reads at a glance. The "Actual"
 * claim has two tones: red when it diverged from the expectation (a bug), green when it matched it (a pass).
 */
const BEHAVIOR_TONES = {
  expected: "border-l-status-pending/60 bg-status-pending/5",
  actualDivergence: "border-l-status-critical/60 bg-status-critical/5",
  actualMatch: "border-l-status-success/60 bg-status-success/5",
} as const;

/** Each evidence source gets an icon and an accent so a reviewer skims the list by kind instead of reading tags. */
interface EvidenceSourceStyle {
  Icon: ComponentType<{ size?: number; className?: string }>;
  accent: string;
  border: string;
}

const EVIDENCE_SOURCE_STYLES: Record<string, EvidenceSourceStyle> = {
  run: { Icon: TerminalWindowIcon, accent: "text-status-pending", border: "border-l-status-pending/60" },
  screenshot: { Icon: ImageIcon, accent: "text-status-pending", border: "border-l-status-pending/60" },
  video: { Icon: VideoCameraIcon, accent: "text-status-pending", border: "border-l-status-pending/60" },
  code: { Icon: FileCodeIcon, accent: "text-violet-accent", border: "border-l-violet-accent/60" },
  diff: { Icon: GitDiffIcon, accent: "text-status-high", border: "border-l-status-high/60" },
};

const FALLBACK_EVIDENCE_STYLE: EvidenceSourceStyle = {
  Icon: InfoIcon,
  accent: "text-text-secondary",
  border: "border-l-border-mid",
};

function evidenceSourceStyle(source: string): EvidenceSourceStyle {
  return EVIDENCE_SOURCE_STYLES[source.toLowerCase().trim()] ?? FALLBACK_EVIDENCE_STYLE;
}

function isBlank(children: ReactNode): boolean {
  return children == null || (typeof children === "string" && children.trim() === "");
}

export function VerdictSectionTitle({
  icon,
  children,
}: {
  /** An optional marker rendered before the label; each section sets one. */
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <h3 className="flex items-center gap-1.5 font-mono text-2xs font-semibold uppercase tracking-widest text-text-secondary">
      {icon}
      {children}
    </h3>
  );
}

/** A titled prose paragraph that renders nothing when its content is absent or blank. */
export function ProseSection({
  title,
  tone = "primary",
  icon,
  children,
}: {
  title: string;
  tone?: "primary" | "secondary";
  icon?: ReactNode;
  children: ReactNode;
}) {
  if (isBlank(children)) return null;
  return (
    <section className="flex flex-col gap-2">
      <VerdictSectionTitle icon={icon}>{title}</VerdictSectionTitle>
      <p className={cn("text-sm leading-relaxed", tone === "secondary" ? "text-text-secondary" : "text-text-primary")}>
        {children}
      </p>
    </section>
  );
}

/** The expected app behavior of an app-health verdict, tinted info-blue. Renders nothing when the field is absent. */
export function ExpectedSection({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <BehaviorClaim title="Expected" tone="expected" icon={icon}>
      {children}
    </BehaviorClaim>
  );
}

/**
 * The actual app behavior of an app-health verdict. Tinted danger-red when it diverged from the expectation (a bug),
 * success-green when it matched it (a pass) so a passing verdict never reads as an error. Renders nothing when the
 * field is absent.
 */
export function ActualSection({
  outcome = "divergence",
  icon,
  children,
}: {
  outcome?: BehaviorOutcome;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <BehaviorClaim title="Actual" tone={outcome === "match" ? "actualMatch" : "actualDivergence"} icon={icon}>
      {children}
    </BehaviorClaim>
  );
}

/**
 * The expected/actual pair laid side-by-side so the contrast reads across, not down - the shape an app-health
 * verdict is judged on. Renders nothing when neither is present (a coverage verdict), and falls back to a single
 * full-width claim when only one side exists rather than leave a lopsided empty column.
 */
export function ExpectedActualSections({
  expected,
  actual,
  outcome = "divergence",
}: {
  expected: ReactNode;
  actual: ReactNode;
  /** Whether the actual behavior matched the expectation (a pass) or contradicted it (a bug). Defaults to a bug. */
  outcome?: BehaviorOutcome;
}) {
  const hasExpected = !isBlank(expected);
  const hasActual = !isBlank(actual);
  if (!hasExpected && !hasActual) return null;
  const expectedIcon = <CheckCircleIcon size={SECTION_ICON_SIZE} className="text-status-pending" />;
  const actualIcon =
    outcome === "match" ? (
      <CheckCircleIcon size={SECTION_ICON_SIZE} className="text-status-success" />
    ) : (
      <XCircleIcon size={SECTION_ICON_SIZE} className="text-status-critical" />
    );
  if (!hasExpected || !hasActual) {
    return (
      <>
        <ExpectedSection icon={expectedIcon}>{expected}</ExpectedSection>
        <ActualSection outcome={outcome} icon={actualIcon}>
          {actual}
        </ActualSection>
      </>
    );
  }
  return (
    <div className="grid grid-cols-2 items-stretch gap-3">
      <ExpectedSection icon={expectedIcon}>{expected}</ExpectedSection>
      <ActualSection outcome={outcome} icon={actualIcon}>
        {actual}
      </ActualSection>
    </div>
  );
}

function BehaviorClaim({
  title,
  tone,
  icon,
  children,
}: {
  title: string;
  tone: keyof typeof BEHAVIOR_TONES;
  icon?: ReactNode;
  children: ReactNode;
}) {
  if (isBlank(children)) return null;
  return (
    <section className={cn("flex flex-col gap-2 rounded-lg border-l-2 px-4 py-3", BEHAVIOR_TONES[tone])}>
      <VerdictSectionTitle icon={icon}>{title}</VerdictSectionTitle>
      <p className="text-sm leading-relaxed text-text-primary">{children}</p>
    </section>
  );
}

/**
 * The neutral callout for app problems the run saw independent of this test's pass/fail. Deliberately quiet (a
 * grey box, not a status tone): these are context on the app, not this verdict's headline, so they must not
 * out-shout the expected/actual claims above them.
 */
export function ObservedAppIssuesNote({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  if (isBlank(children)) return null;
  return (
    <div className="rounded-lg border border-border-dim bg-surface-raised px-4 py-3 text-sm leading-relaxed text-text-secondary">
      {icon != null && <span className="mr-1.5 inline-flex translate-y-0.5 text-text-secondary">{icon}</span>}
      <span className="font-medium text-text-primary">App issues observed: </span>
      {children}
    </div>
  );
}

/** The verbatim classifier error, shown in place of the verdict fields when the model failed to classify. */
export function ClassificationErrorBlock({ icon, error }: { icon?: ReactNode; error: string }) {
  return (
    <section className="flex flex-col gap-2">
      <VerdictSectionTitle icon={icon}>Classification error</VerdictSectionTitle>
      <pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-surface-void p-4 font-mono text-2xs text-text-secondary">
        {error}
      </pre>
    </section>
  );
}

/**
 * The finding's code evidence, as a colored card per item keyed by its source. `collapsible` folds the whole list
 * behind its title (the drawer, whose narrow panel would otherwise be a wall of snippets); the full page leaves it
 * expanded. `repoFullName` / `commitSha` are optional - without them the permalinks simply do not render.
 */
export function VerdictEvidence({
  evidence,
  collapsible = false,
  icon,
  repoFullName,
  commitSha,
}: {
  evidence: InvestigationEvidence[];
  collapsible?: boolean;
  icon?: ReactNode;
  repoFullName?: string;
  commitSha?: string;
}) {
  const [open, setOpen] = useState(!collapsible);
  if (evidence.length === 0) return null;

  // A masonry so the mixed cards (a one-line run note, a tall code snippet, a run frame) tile by height instead
  // of leaving a ragged single column. Container-query driven, not viewport: it splits to two columns only once
  // its own box is wide (the finding page's main column), staying single-column in the narrow drawer that also
  // renders this list. Each card avoids breaking across the column gap.
  const items = (
    <div className="@container">
      <div className="columns-1 gap-2 @2xl:columns-2">
        {evidence.map((item, i) => (
          <div key={i} className="mb-2 break-inside-avoid">
            <EvidenceItem item={item} repoFullName={repoFullName} commitSha={commitSha} />
          </div>
        ))}
      </div>
    </div>
  );

  if (!collapsible) {
    return (
      <section className="flex flex-col gap-2">
        <VerdictSectionTitle icon={icon}>Evidence</VerdictSectionTitle>
        {items}
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex items-center gap-2 text-left"
      >
        <VerdictSectionTitle icon={icon}>Evidence</VerdictSectionTitle>
        <span className="font-mono text-3xs text-text-secondary">{evidence.length}</span>
        <CaretDownIcon size={12} className={cn("text-text-secondary transition-transform", open && "rotate-180")} />
      </button>
      {open && items}
    </section>
  );
}

function EvidenceItem({
  item,
  repoFullName,
  commitSha,
}: {
  item: InvestigationEvidence;
  repoFullName?: string;
  commitSha?: string;
}) {
  const { Icon, accent, border } = evidenceSourceStyle(item.source);
  const permalink = evidencePermalink(item, repoFullName, commitSha);
  const snippet = item.snippet;
  const hasSnippet = snippet != null && snippet !== "";
  const fileLabel =
    item.file != null
      ? `${item.repo != null ? `${item.repo} › ` : ""}${item.file}${item.lines != null ? `:${item.lines}` : ""}`
      : undefined;
  return (
    <div className={cn("overflow-hidden rounded-md border border-l-2 border-border-dim bg-surface-void", border)}>
      <div className="flex items-center gap-2 px-3 py-2">
        <Icon size={13} className={cn("shrink-0", accent)} />
        <span className={cn("font-mono text-3xs uppercase tracking-wider", accent)}>{item.source}</span>
        {/* Snippet-bearing items show their file in the code block's own header, so only the snippet-less ones
            surface the file here - otherwise the path would appear twice. */}
        {!hasSnippet &&
          fileLabel != null &&
          (permalink != null ? (
            <a
              href={permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate font-mono text-3xs text-text-secondary hover:text-text-primary hover:underline"
            >
              {fileLabel}
            </a>
          ) : (
            <span className="truncate font-mono text-3xs text-text-secondary">{fileLabel}</span>
          ))}
      </div>
      {item.detail !== "" && <p className="px-3 pb-2 text-sm leading-relaxed text-text-secondary">{item.detail}</p>}
      {item.frameUrl != null && (
        <div className="px-3 pb-3">
          <span className="inline-block w-fit overflow-hidden rounded border border-border-dim">
            <ScreenshotLightbox
              src={item.frameUrl}
              alt={`Run frame cited by this ${item.source} evidence`}
              className="h-40 w-auto"
            />
          </span>
        </div>
      )}
      {hasSnippet && (
        <div className="px-3 pb-3">
          <CodeBlock code={snippet} file={item.file} lines={item.lines} permalink={permalink} />
        </div>
      )}
    </div>
  );
}
